import { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import PageContainer from 'components/PageContainer';
import { Button } from '@mui/material';
import AddExitDetails, { FormData } from './AddDetail';
import { EXITS } from 'adminConstants/routes';
import Summary from '../Common/Summary';
import useExitStatement from 'hooks/data/exit/useExitStatement';
import useStyles from '../Common/styles';
import PageContent from 'components/PageContent';
import ButtonGroup from 'components/ButtonGroup';
import RecordView from 'components/RecordView';
import api from 'lib/trpcClient';
import { CalendarDay } from 'further-ui/utils';

type Params = {
  id: string;
  exitAmendmentId?: string;
};

const CreateExitStatement = () => {
  const history = useHistory();
  const { classes } = useStyles();
  const heading = 'Investor communications: Exit statement';
  const breadcrumbs = [
    { label: 'Dashboard', link: '/' },
    { label: 'Exit statement', link: '/', isActive: true },
  ];

  const { id, exitAmendmentId } = useParams<Params>();

  const steps = { addExitStatementDetails: 1, summary: 2 };
  const [activeStep, setActiveStep] = useState(steps.addExitStatementDetails);

  const { createExitStatement } = useExitStatement({});

  const exit = api.exit.getById.useQuery(
    { id: id ?? '' },
    {
      enabled: !!id,
    },
  );

  const draftExitStatement = api.exitStatement.getDraft.useQuery(
    {
      exitId: id ?? '',
      exitAmendmentId: exitAmendmentId ?? undefined,
    },
    {
      enabled: !!id,
      retry: false,
    },
  );

  const previousExitStatement =
    api.exitStatement.getPreviousExitStatement.useQuery(
      {
        exitId: id ?? '',
      },
      {
        enabled:
          !!id &&
          draftExitStatement.isFetched &&
          (draftExitStatement.data === null ||
            draftExitStatement.data === undefined),
        retry: false,
      },
    );

  const previousExitStatementForCompanyLegalNameExists =
    api.exitStatement.checkPreviousExitStatementForCompanyLegalName.useQuery(
      {
        exitId: id ?? '',
      },
      {
        enabled: !!id,
        retry: false,
      },
    );

  const [formValues, setFormValues] = useState<
    FormData & {
      notifyInvestors?: boolean;
      includePreviousExitStatements?: boolean;
    }
  >({
    statementDate: new Date(),
    title: '',
    summary: '',
    letter: '',
    notes: '',
    supportingFiles: [],
    notifyInvestors: false,
    includePreviousExitStatements: false,
  });

  // Shared function to prefill form values from exit statement data
  const prefillFormValues = (
    exitStatementData: {
      title?: string | null;
      summary?: string | null;
      letter?: string | null;
      notes?: string | null;
      supportingFiles?: Array<{ name: string; path: string }>;
      statementDate?: string | null;
    } | null,
  ) => {
    if (!exitStatementData) return;

    setFormValues((prev) => ({
      ...prev,
      title: exitStatementData.title || prev.title,
      summary: exitStatementData.summary || prev.summary,
      letter: exitStatementData.letter || prev.letter,
      notes: exitStatementData.notes || prev.notes,
      supportingFiles: exitStatementData.supportingFiles
        ? exitStatementData.supportingFiles.map((file) => ({
            name: file.name,
            path: file.path,
            preview: file.path,
          }))
        : prev.supportingFiles,
      statementDate: exitStatementData.statementDate
        ? new Date(exitStatementData.statementDate)
        : prev.statementDate,
    }));
  };

  useEffect(() => {
    if (exit.data?.date) {
      setFormValues((prev) => ({
        ...prev,
        statementDate: new Date(exit.data?.date ?? new Date()),
      }));
    }
  }, [exit.data?.date]);

  // Prefill from draft if it exists, otherwise use previous statement
  useEffect(() => {
    if (draftExitStatement.data) {
      prefillFormValues(draftExitStatement.data);
    } else if (previousExitStatement.data) {
      prefillFormValues(previousExitStatement.data);
    }
  }, [draftExitStatement.data, previousExitStatement.data]);

  const handleNext = () => {
    setActiveStep((currentStep) => currentStep + 1);
  };

  const handleSubmitDetails = (values: FormData) => {
    // Preserve notifyInvestors and includePreviousExitStatements across steps
    setFormValues({
      ...formValues,
      ...values,
    });
    handleNext();
  };

  const handleSubmit = () => {
    if (!formValues || !id || !formValues.statementDate) return;

    createExitStatement.mutate({
      exitId: id,
      exitAmendmentId,
      data: {
        title: formValues.title,
        summary: formValues.summary,
        letter: formValues.letter,
        notes: formValues.notes,
        supportingFiles: formValues.supportingFiles,
        notifyInvestors: formValues.notifyInvestors || false,
        statementDate: new CalendarDay(formValues.statementDate),
        includePreviousExitStatements:
          formValues.includePreviousExitStatements || false,
      },
    });
  };

  useEffect(() => {
    if (createExitStatement.isSuccess) {
      history.push(EXITS);
    }
  }, [createExitStatement.isSuccess]);

  const pageTitle =
    activeStep === steps.addExitStatementDetails
      ? 'Exit statement details'
      : 'Exit statement summary';

  return (
    <PageContainer heading={heading} breadcrumbs={breadcrumbs}>
      <PageContent header={pageTitle}>
        <RecordView>
          {steps.addExitStatementDetails === activeStep ? (
            <AddExitDetails
              onNext={handleSubmitDetails}
              defaultValues={formValues}
              notifyInvestors={formValues.notifyInvestors}
              setNotifyInvestors={(value) =>
                setFormValues({ ...formValues, notifyInvestors: value })
              }
              previousExitStatementForCompanyLegalNameExists={
                previousExitStatementForCompanyLegalNameExists.data ?? false
              }
              includePreviousExitStatementsValue={
                formValues.includePreviousExitStatements
              }
              setIncludePreviousExitStatements={(value) =>
                setFormValues({
                  ...formValues,
                  includePreviousExitStatements: value,
                })
              }
              tradingName={exit.data?.companies?.[0]?.tradingName ?? ''}
            />
          ) : (
            <Summary
              statementDate={formValues.statementDate}
              title={formValues.title}
              summary={formValues.summary}
              letter={formValues.letter}
              notes={formValues.notes}
              supportingFiles={formValues.supportingFiles}
            />
          )}
          {activeStep === steps.summary && (
            <ButtonGroup>
              <Button
                variant="outlined"
                onClick={() => {
                  setActiveStep(steps.addExitStatementDetails);
                }}
              >
                Back
              </Button>

              <Button
                className={classes.buttonLeftMargin}
                color="primary"
                variant="contained"
                disabled={createExitStatement.isPending}
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </ButtonGroup>
          )}
        </RecordView>
      </PageContent>
    </PageContainer>
  );
};
export default CreateExitStatement;
